import { c as _c } from "react/compiler-runtime";
import { useMutation } from '@apollo/client/react';
import { gql } from '@src/lib/gql';
import { useEffect, useEffectEvent, useRef } from 'react';

/** Typing reads as stopped this long after the last keystroke. */
export const TYPING_IDLE_MS = 3000;
const UPSERT_ATTACHMENT_USER_PRESENCE = gql`
  mutation ($input: UpsertAttachmentUserPresenceInput!) {
    upsertAttachmentUserPresence(input: $input) {
      success
    }
  }
`;

/**
 * The composer's presence: online when it mounts, typing from the first keystroke until the box is
 * empty or TYPING_IDLE_MS pass without one. Sends only on those changes, never per keystroke, and
 * keeps its state in refs, so typing does not re-render the composer.
 */
export function useTypingPresence(input) {
  const $ = _c(11);
  const [upsertPresence] = useMutation(UPSERT_ATTACHMENT_USER_PRESENCE);
  const typing = useRef(false);
  const idleTimer = useRef(0);
  const latest = useRef(input);
  let t0;
  if ($[0] !== input) {
    t0 = () => {
      latest.current = input;
    };
    $[0] = input;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  useEffect(t0);
  let t1;
  if ($[2] !== upsertPresence) {
    t1 = isTyping => {
      const {
        attachmentType,
        attachmentId,
        userId
      } = latest.current;
      if (attachmentType && attachmentId) {
        upsertPresence({
          variables: {
            input: {
              attachmentType,
              attachmentId,
              isTyping,
              isOnline: true,
              userId
            }
          }
        });
      }
    };
    $[2] = upsertPresence;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const send = t1;
  let t2;
  if ($[4] !== send) {
    t2 = () => send(false);
    $[4] = send;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const announceOnline = useEffectEvent(t2);
  let t3;
  if ($[6] !== announceOnline) {
    t3 = () => {
      announceOnline();
      return () => window.clearTimeout(idleTimer.current);
    };
    $[6] = announceOnline;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = [];
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  useEffect(t3, t4);
  let t5;
  if ($[9] !== send) {
    const noteText = hasText => {
      window.clearTimeout(idleTimer.current);
      if (!hasText) {
        if (typing.current) {
          typing.current = false;
          send(false);
        }
        return;
      }
      if (!typing.current) {
        typing.current = true;
        send(true);
      }
      idleTimer.current = window.setTimeout(() => {
        typing.current = false;
        send(false);
      }, TYPING_IDLE_MS);
    };
    t5 = {
      noteText
    };
    $[9] = send;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}